import { ReactNode, useCallback, useRef } from 'react';
import { ConfigProvider } from 'antd';
import { useAgent } from './context';

export function AgentLayout({ children }: { children: ReactNode }) {
  const agent = useAgent();
  const sidebar = agent && !agent.fullPage && agent.mode === 'sidebar';
  const businessRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const getPopupContainer = useCallback(() => businessRef.current ?? document.body, []);
  const getTargetContainer = useCallback(() => scrollRef.current ?? document.body, []);
  return (
    <div
      className={`fett-agent-layout${sidebar ? ' has-sidebar' : ''}`}
      style={{
        gridTemplateColumns: sidebar
          ? `minmax(0, 1fr) min(${agent.sidebarWidth}px, 55%)`
          : 'minmax(0, 1fr)',
      }}
    >
      <div ref={businessRef} className="fett-agent-business">
        <ConfigProvider
          // Only the sidebar needs confined popups. Otherwise inherit AppContent's containers.
          getPopupContainer={sidebar ? getPopupContainer : undefined}
          getTargetContainer={sidebar ? getTargetContainer : undefined}
        >
          <div ref={scrollRef} className="fett-agent-business-scroll">
            <div className="fett-agent-business-content">{children}</div>
          </div>
        </ConfigProvider>
        <div ref={agent?.setBusinessContainer} />
      </div>
      {sidebar && (
        <aside className="fett-agent-sidebar">
          <div
            className="fett-agent-divider"
            role="separator"
            aria-orientation="vertical"
            aria-valuemin={300}
            aria-valuemax={720}
            aria-valuenow={agent.sidebarWidth}
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft')
                agent.setSidebarWidth(Math.min(720, agent.sidebarWidth + 20));
              if (event.key === 'ArrowRight')
                agent.setSidebarWidth(Math.max(300, agent.sidebarWidth - 20));
            }}
            onPointerDown={(event) => event.currentTarget.setPointerCapture(event.pointerId)}
            onPointerMove={(event) => {
              if (event.currentTarget.hasPointerCapture(event.pointerId))
                agent.setSidebarWidth(
                  Math.max(
                    300,
                    Math.min(
                      720,
                      event.currentTarget.parentElement!.parentElement!.getBoundingClientRect()
                        .right - event.clientX,
                    ),
                  ),
                );
            }}
            onPointerUp={(event) => event.currentTarget.releasePointerCapture(event.pointerId)}
          />
          <div ref={agent.setSidebarContainer} className="fett-agent-sidebar-content" />
        </aside>
      )}
    </div>
  );
}
